import { Field, Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import TextField from 'Containers/Common/TextField';
import HoveredInfoIcon from 'Containers/Common/HoveredInfoIcon';
import styles from './ExpenseModal.module.scss';
import { formatEuro, getStaffBonus } from '../utils';
import { expensesActions } from '../store';
import dayjs from 'dayjs';

const CATEGORY_KEYS = [
  'bollette',
  'affitto',
  'materiali',
  'manutenzione',
  'marketing',
  'tasse_commissioni',
  'altro',
];

const ChairRevenueSync = ({ poltronaId, data, enabled, onRevenue, onLoading }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!enabled || !poltronaId || !data) {
      onLoading(false);
      onRevenue(null);
      return;
    }

    let active = true;
    onLoading(true);
    onRevenue(null);

    dispatch(
      expensesActions.GET_CHAIR_REVENUE.REQUEST(
        {
          idPoltrona: poltronaId,
          month: dayjs(data).format('YYYY-MM'),
        },
        (result) => {
          if (!active) return;
          onRevenue(result?.fatturato ?? 0);
          onLoading(false);
        },
      ),
    );

    return () => {
      active = false;
    };
  }, [poltronaId, data, enabled, dispatch, onRevenue, onLoading]);

  return null;
};

const ExpenseModal = ({ expense, chairs, period, onSave, closeModal }) => {
  const { t, i18n } = useTranslation();
  const [chairRevenue, setChairRevenue] = useState(null);
  const [chairRevenueLoading, setChairRevenueLoading] = useState(false);
  const handleRevenue = useCallback((value) => setChairRevenue(value), []);
  const handleRevenueLoading = useCallback((value) => setChairRevenueLoading(value), []);
  const defaultDate = period?.isSame(dayjs(), 'month')
    ? dayjs().format('YYYY-MM-DD')
    : dayjs(period).startOf('month').format('YYYY-MM-DD');

  const defaultPeriodicaFine = dayjs(expense?.data || defaultDate)
    .startOf('month')
    .add(18, 'month')
    .format('YYYY-MM');

  const initialValues = {
    tipo: expense?.tipo || 'staff',
    stato: expense?.stato || 'pagato',
    poltronaId: expense?.poltronaId || chairs[0]?.id || '',
    data: expense?.data || defaultDate,
    importoBase: expense?.importoBase ?? '',
    bonusPct: expense?.bonusPct ?? '',
    categoria: expense?.categoria || 'bollette',
    importo: expense?.importo ?? '',
    note: expense?.note || '',
    periodica: Boolean(expense?.periodica || expense?.recurrenceId),
    periodicaOgni: expense?.periodicaOgni ?? 1,
    periodicaFine: expense?.periodicaFine || defaultPeriodicaFine,
  };

  const validationSchema = yup.object().shape({
    tipo: yup.string().required(),
    data: yup.string().required(t('settings.expenses.validation.dateRequired')),
    poltronaId: yup.mixed().when('tipo', ([tipo], schema) =>
      tipo === 'staff' ? schema.required(t('settings.expenses.validation.chairRequired')) : schema,
    ),
    importoBase: yup.number().when('tipo', ([tipo], schema) =>
      tipo === 'staff'
        ? schema
            .transform((value, original) => {
              if (original === '' || original == null) return undefined;
              const normalized = String(original).replace(',', '.');
              const n = parseFloat(normalized);
              return isNaN(n) ? undefined : n;
            })
            .typeError(t('settings.expenses.validation.salaryRequired'))
            .positive(t('settings.expenses.validation.salaryRequired'))
            .required(t('settings.expenses.validation.salaryRequired'))
        : schema.notRequired(),
    ),
    importo: yup.number().when('tipo', ([tipo], schema) =>
      tipo === 'operativa'
        ? schema
            .transform((value, original) => {
              if (original === '' || original == null) return undefined;
              const normalized = String(original).replace(',', '.');
              const n = parseFloat(normalized);
              return isNaN(n) ? undefined : n;
            })
            .typeError(t('settings.expenses.validation.amountRequired'))
            .positive(t('settings.expenses.validation.amountRequired'))
            .required(t('settings.expenses.validation.amountRequired'))
        : schema.notRequired(),
    ),
  });

  const chairOptions = chairs.map((chair) => ({
    name: chair.name,
    value: chair.id,
    icon: chair.icon,
  }));

  const categoryOptions = CATEGORY_KEYS.map((key) => ({
    name: t(`settings.expenses.categories.${key}`),
    value: key,
  }));

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const parseNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;
        const periodicaFine = dayjs(values.data || defaultDate)
          .startOf('month')
          .add(18, 'month')
          .format('YYYY-MM');
        const payload =
          values.tipo === 'staff'
            ? {
                tipo: 'staff',
                data: values.data,
                poltronaId: values.poltronaId,
                importoBase: parseNum(values.importoBase),
                bonusPct: parseNum(values.bonusPct),
                fatturatoMese: chairRevenue ?? 0,
                stato: values.stato,
                periodica: values.periodica,
                ...(values.periodica && {
                  periodicaOgni: Number(values.periodicaOgni) || 1,
                  periodicaFine,
                }),
              }
            : {
                tipo: 'operativa',
                data: values.data,
                categoria: values.categoria,
                importo: parseNum(values.importo),
                note: values.note,
                stato: values.stato,
                periodica: values.periodica,
                ...(values.periodica && {
                  periodicaOgni: Number(values.periodicaOgni) || 1,
                  periodicaFine,
                }),
              };
        onSave(payload);
      }}
    >
      {({ values, setFieldValue, touched, errors }) => {
        const parseNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;
        const draftStaff = {
          tipo: 'staff',
          poltronaId: values.poltronaId,
          importoBase: parseNum(values.importoBase),
          bonusPct: parseNum(values.bonusPct),
          fatturatoMese: chairRevenue ?? 0,
        };
        const bonus = getStaffBonus(draftStaff, chairs);
        const total = parseNum(values.importoBase) + bonus;
        const revenueLabel = chairRevenueLoading
          ? null
          : chairRevenue == null && values.poltronaId
            ? '...'
            : formatEuro(chairRevenue ?? 0, i18n.language);

        const RecurringSection = () => {
          const PRESETS = [1, 2, 3, 6, 12];
          const lang = i18n.language;
          const monthLabel = (n) => {
            if (lang === 'fr') return n === 1 ? '1 mois' : `${n} mois`;
            if (lang === 'en') return n === 1 ? '1 month' : `${n} months`;
            return n === 1 ? '1 mese' : `${n} mesi`;
          };
          const defaultFine = dayjs(values.data || defaultDate)
            .startOf('month')
            .add(18, 'month')
            .format('YYYY-MM');

          return (
            <div className={styles.recurring_block}>
              <div className={styles.recurring_header}>
                <button
                  type="button"
                  className={`${styles.recurring_toggle} ${values.periodica ? styles.active : ''}`}
                  onClick={() => {
                    // In modifica di una serie esistente non si può togliere il flag ricorrente da qui
                    if (expense?.recurrenceId && values.periodica) return;
                    const next = !values.periodica;
                    setFieldValue('periodica', next);
                    if (next) setFieldValue('periodicaFine', defaultFine);
                  }}
                  disabled={Boolean(expense?.recurrenceId && values.periodica)}
                >
                  <span className={styles.recurring_check}>{values.periodica ? '✓' : ''}</span>
                  {t('settings.expenses.form.isRecurring')}
                </button>
                <HoveredInfoIcon
                  description={t('settings.expenses.form.recurringInfo')}
                  sidePosition="right"
                />
              </div>

              {values.periodica && (
                <div className={styles.recurring_every_row}>
                  <span className={styles.recurring_every_label}>
                    {t('settings.expenses.form.recurringEvery')}
                  </span>
                  <select
                    className={styles.native_select}
                    value={values.periodicaOgni}
                    onChange={(e) => setFieldValue('periodicaOgni', Number(e.target.value))}
                    disabled={Boolean(expense?.recurrenceId)}
                  >
                    {PRESETS.map((n) => (
                      <option key={n} value={n}>{monthLabel(n)}</option>
                    ))}
                  </select>
                </div>
              )}
              {expense?.recurrenceId && (
                <div className={styles.hint}>
                  {t('settings.expenses.form.recurringEditHint')}
                </div>
              )}
            </div>
          );
        };

        return (
          <Form className={styles.expense_modal}>
            <ChairRevenueSync
              poltronaId={values.poltronaId}
              data={values.data}
              enabled={values.tipo === 'staff'}
              onRevenue={handleRevenue}
              onLoading={handleRevenueLoading}
            />
            <div className={styles.body}>
              <div className={styles.type_toggle}>
                <button
                  type="button"
                  disabled={!!expense}
                  className={`${styles.toggle_button} ${values.tipo === 'staff' ? styles.active : ''}`}
                  onClick={() => setFieldValue('tipo', 'staff')}
                >
                  {t('settings.expenses.types.staff')}
                  <span className={styles.sub}>{t('settings.expenses.types.staffSub')}</span>
                </button>
                <button
                  type="button"
                  disabled={!!expense}
                  className={`${styles.toggle_button} ${values.tipo === 'operativa' ? styles.active : ''}`}
                  onClick={() => setFieldValue('tipo', 'operativa')}
                >
                  {t('settings.expenses.types.operative')}
                  <span className={styles.sub}>{t('settings.expenses.types.operativeSub')}</span>
                </button>
              </div>

              {values.tipo === 'staff' ? (
                <>
                  <div>
                    <label className={styles.select_label_top}>{t('settings.expenses.form.chair')}</label>
                    <select
                      className={`${styles.native_select} ${touched.poltronaId && errors.poltronaId ? styles.select_error : ''}`}
                      value={values.poltronaId}
                      onChange={(e) => setFieldValue('poltronaId', e.target.value)}
                    >
                      <option value="" disabled>{t('settings.expenses.form.chairPlaceholder')}</option>
                      {chairOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.name}</option>
                      ))}
                    </select>
                    {touched.poltronaId && errors.poltronaId && (
                      <div className={styles.select_error_text}>{errors.poltronaId}</div>
                    )}
                  </div>
                  <div>
                    <Field
                      name="data"
                      type="date"
                      label={t('settings.expenses.form.period')}
                      component={TextField}
                      height="50px"
                      margin="0px"
                      inputColor="#ffffff"
                      border="1px solid #e0e0e0"
                    />
                    <div className={styles.hint}>{t('settings.expenses.form.periodHint')}</div>
                  </div>
                  <div className={styles.info_box}>
                    <span className={styles.k}>{t('settings.expenses.form.revenue')}</span>
                    {chairRevenueLoading ? (
                      <span className={styles.mini_skel} aria-hidden />
                    ) : (
                      <span className={styles.v}>{revenueLabel}</span>
                    )}
                  </div>
                  <Field
                    name="importoBase"
                    type="text"
                    inputMode="decimal"
                    label={t('settings.expenses.form.baseSalary')}
                    component={TextField}
                    height="50px"
                    margin="0px"
                    placeholder="0"
                    error={touched.importoBase && errors.importoBase}
                  />
                  <div>
                    <Field
                      name="bonusPct"
                      type="text"
                      inputMode="decimal"
                      label={t('settings.expenses.form.bonus')}
                      component={TextField}
                      height="50px"
                      margin="0px"
                      placeholder="0"
                    />
                    <div className={styles.hint}>{t('settings.expenses.form.bonusHint')}</div>
                  </div>
                  {bonus > 0 && (
                    <div className={styles.total_row}>
                      <div className={styles.total_line}>
                        <span className={styles.k}>
                          {t('settings.expenses.form.bonusPreview', { pct: values.bonusPct })}
                        </span>
                        <span className={styles.bonus_v}>+{formatEuro(bonus, i18n.language)}</span>
                      </div>
                      <div className={styles.total_line}>
                        <span className={styles.k}>{t('settings.expenses.form.totalCompensation')}</span>
                        <span className={styles.v}>{formatEuro(total, i18n.language)}</span>
                      </div>
                    </div>
                  )}
                  <RecurringSection />
                </>
              ) : (
                <>
                  <div>
                    <label className={styles.select_label_top}>{t('settings.expenses.form.category')}</label>
                    <select
                      className={styles.native_select}
                      value={values.categoria}
                      onChange={(e) => setFieldValue('categoria', e.target.value)}
                    >
                      {categoryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.form_row}>
                    <Field
                      name="importo"
                      type="text"
                      inputMode="decimal"
                      label={t('settings.expenses.form.amount')}
                      component={TextField}
                      height="50px"
                      margin="0px"
                      placeholder="0"
                      error={touched.importo && errors.importo}
                    />
                    <Field
                      name="data"
                      type="date"
                      label={t('settings.expenses.form.date')}
                      component={TextField}
                      height="50px"
                      margin="0px"
                      inputColor="#ffffff"
                      border="1px solid #e0e0e0"
                    />
                  </div>
                  <Field
                    name="note"
                    textarea
                    label={t('settings.expenses.form.notes')}
                    component={TextField}
                    height="80px"
                    margin="0px"
                    placeholder={t('settings.expenses.form.notesPlaceholder')}
                  />

                  <RecurringSection />
                </>
              )}

              <div>
                <div className={styles.hint} style={{ marginBottom: 8 }}>
                  {t('settings.expenses.form.paymentStatus')}
                </div>
                <div className={styles.status_toggle}>
                  <button
                    type="button"
                    className={`${styles.toggle_button} ${styles.paid} ${values.stato === 'pagato' ? styles.active : ''}`}
                    onClick={() => setFieldValue('stato', 'pagato')}
                  >
                    {t('settings.expenses.status.paid')}
                  </button>
                  <button
                    type="button"
                    className={`${styles.toggle_button} ${styles.due} ${values.stato === 'da_pagare' ? styles.active : ''}`}
                    onClick={() => setFieldValue('stato', 'da_pagare')}
                  >
                    {t('settings.expenses.status.due')}
                  </button>
                </div>
              </div>
            </div>
            <hr />
            <div className={styles.footer}>
              <Button type="button" className={styles.ghost_button} onClick={closeModal}>
                {t('common.actions.cancel')}
              </Button>
              <Button type="submit" className={styles.confirm_button} variant="contained">
                {t('common.actions.save')}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ExpenseModal;
